import React from "react";
import type { ColumnDef } from "@tanstack/react-table";

import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/ui/data-table";
import { DataTableShell } from "@/components/ui/data-table-shell";
import type {
  AnomalyDetail,
  CommandDetail,
  GapDetail,
  ServiceDetail,
  StateChangeDetail,
  TelemetryEvent,
  TelemetryEventKind,
} from "@/lib/telemetry";
import { formatNumber, formatSeconds, formatTs, type TelemetryTz } from "@/components/telemetry/format";
import { cn } from "@/lib/utils";

/**
 * One line describing what happened.
 *
 * Descriptive, never diagnostic: "voltage 0 while charging" is a fact in the
 * telemetry; whether that was a tripped protection or a dropped contactor is
 * not something the telemetry knows, and labelling it here would be a guess
 * wearing a fact's clothes.
 */
export function eventDetailText(event: TelemetryEvent): string {
  const detail = event.detail;
  if (!detail) return "—";

  switch (event.kind) {
    case "state_change": {
      const d = detail as StateChangeDetail;
      const previous = d.prev_duration_s != null ? ` (after ${formatSeconds(d.prev_duration_s)})` : "";
      return `${d.from ?? "—"} → ${d.to ?? "—"}${previous}`;
    }
    case "command": {
      const d = detail as CommandDetail;
      const decoded = d.decoded as Record<string, unknown> | null;
      if (decoded && Object.keys(decoded).length) {
        const parts: string[] = [];
        if (decoded.day || decoded.time) parts.push(`${decoded.day ?? ""} ${decoded.time ?? ""}`.trim());
        if (decoded.online_current != null) parts.push(`online ${decoded.online_current}A`);
        if (decoded.offline_current != null) parts.push(`offline ${decoded.offline_current}A`);
        if (decoded.rapid_mode != null) parts.push(`rapid ${decoded.rapid_mode ? "ON" : "OFF"}`);
        if (parts.length) return parts.join(" · ");
      }
      return d.raw ?? "—";
    }
    case "gap": {
      const d = detail as GapDetail;
      const kind = d.severity === "offline_looking" ? "offline-looking" : "short";
      return `no samples for ${formatSeconds(d.seconds)} (${kind}, offline threshold ${formatSeconds(d.offline_threshold_s)})`;
    }
    case "service": {
      const d = detail as ServiceDetail;
      const marker = d.marker || "service";
      return marker === "REJECTED"
        ? "rejected on checksum — not processed"
        : `${marker} frame — no telemetry`;
    }
    case "anomaly": {
      const d = detail as AnomalyDetail;
      const observed = Object.entries(d.observed ?? {})
        .map(([key, value]) => `${key}=${Array.isArray(value) ? value.join("/") : value}`)
        .join(" ");
      return `${d.rule}${d.sample_count > 1 ? ` ×${d.sample_count}` : ""}${observed ? ` · ${observed}` : ""}`;
    }
    default:
      return "—";
  }
}

function anomalySeverity(event: TelemetryEvent): "error" | "warning" {
  const detail = event.detail as AnomalyDetail | null;
  return detail?.severity === "error" ? "error" : "warning";
}

/**
 * What the frame is, expressed as colour rather than as a column.
 *
 * The kind of an event is a fact about the pipeline, not about the charger; a
 * support reader needs to see that a row is an anomaly, not to read the word.
 */
type FrameTone = "error" | "warning" | "gap" | "plain";

function frameTone(row: EventFrameRow): FrameTone {
  const anomaly = row.events.find((e) => e.kind === "anomaly");
  if (anomaly) return anomalySeverity(anomaly);
  if (row.kinds.includes("gap")) return "gap";
  return "plain";
}

/** Soft enough to read a whole table of them, loud enough to find one. */
const TONE_ROW_CLASS: Record<FrameTone, string> = {
  error: "bg-red-500/10 hover:bg-red-500/15",
  warning: "bg-amber-400/10 hover:bg-amber-400/15",
  gap: "bg-amber-400/5 hover:bg-amber-400/10",
  plain: "",
};

const TONE_TEXT_CLASS: Record<FrameTone, string> = {
  error: "text-red-600 dark:text-red-400",
  warning: "text-amber-700 dark:text-amber-400",
  gap: "text-amber-700 dark:text-amber-400",
  plain: "",
};

export function eventRefString(event: TelemetryEvent): string | null {
  const { src_file, src_line } = event.ref ?? {};
  if (!src_file || src_line == null) return null;
  return `${src_file}:${src_line}`;
}

/**
 * One archived frame, with every event derived from it.
 *
 * `message_logger.py:254` writes one CSV row per UDP exchange, carrying BOTH
 * what the charger reported and the command the server answered with. The lake
 * derives several typed events from that one row — a `sample`, a `command`, and
 * whichever of `state_change` / `gap` / `anomaly` applies — and they all inherit
 * that row's `src_file:src_line`, because a ref addresses the source line, not
 * the event. Listing them as separate rows repeats one timestamp, one state, one
 * set of readings and one ref, and reads as duplication of the frame rather than
 * as two sides of it.
 */
export interface EventFrameRow {
  key: string;
  ts: string;
  state: string | null;
  voltage: number | null;
  current: number | null;
  temperature: number | null;
  ref: string | null;
  kinds: TelemetryEventKind[];
  /** What the charger sent: the detail of every non-command event on this frame. */
  sample: string;
  /** What the server sent back on the same frame. */
  response: string;
  /** What a click opens — the most specific event of the frame. */
  primary: TelemetryEvent;
  events: TelemetryEvent[];
}

/** Most specific first — an anomaly beats the bare sample that carried it. */
const FRAME_PRIORITY: Record<TelemetryEventKind, number> = {
  anomaly: 0,
  gap: 1,
  state_change: 2,
  command: 3,
  sample: 4,
  // Never actually reached: service frames have their own table and are filtered
  // out before a frame row is built. Ranked last so that a future path which
  // does let one through cannot outrank a real event.
  service: 5,
};

/**
 * Collapse events onto the frame they came from, preserving the incoming order.
 *
 * Grouped on ref AND timestamp: an event with no ref cannot be shown to share a
 * source line with anything, so it stays its own row rather than being merged on
 * a guess.
 */
export function mergeEventsByFrame(events: TelemetryEvent[]): EventFrameRow[] {
  const rows: EventFrameRow[] = [];
  const byFrame = new Map<string, EventFrameRow>();

  events.forEach((event, index) => {
    const ref = eventRefString(event);
    const key = ref ? `${ref}@${event.ts}` : `orphan-${index}`;
    const existing = ref ? byFrame.get(key) : undefined;
    const row = existing ?? {
      key,
      ts: event.ts,
      state: event.state,
      voltage: event.voltage,
      current: event.current,
      temperature: event.temperature,
      ref,
      kinds: [],
      sample: "",
      response: "",
      primary: event,
      events: [],
    };

    row.events.push(event);
    if (!row.kinds.includes(event.kind)) row.kinds.push(event.kind);
    if (FRAME_PRIORITY[event.kind] < FRAME_PRIORITY[row.primary.kind]) row.primary = event;

    const text = eventDetailText(event);
    if (event.kind === "command") {
      if (text !== "—") row.response = row.response ? `${row.response} · ${text}` : text;
    } else if (text !== "—") {
      row.sample = row.sample ? `${row.sample} · ${text}` : text;
    }
    // A frame's readings are the frame's, but a bare `sample` event is the one
    // that always carries them — never let a null from a sibling win.
    row.state ??= event.state;
    row.voltage ??= event.voltage;
    row.current ??= event.current;
    row.temperature ??= event.temperature;

    if (!existing) {
      rows.push(row);
      if (ref) byFrame.set(key, row);
    }
  });

  for (const row of rows) {
    row.kinds.sort((a, b) => FRAME_PRIORITY[a] - FRAME_PRIORITY[b]);
  }
  return rows;
}

export function EventsTable({
  events,
  tz,
  isLoading,
  hasMore,
  isLoadingMore,
  onLoadMore,
  onSelect,
  totalEvents,
}: {
  events: TelemetryEvent[];
  tz: TelemetryTz;
  isLoading?: boolean;
  hasMore?: boolean;
  isLoadingMore?: boolean;
  onLoadMore?: () => void;
  onSelect?: (event: TelemetryEvent) => void;
  totalEvents?: number | null;
}) {
  const rows = React.useMemo(() => mergeEventsByFrame(events), [events]);

  const columns = React.useMemo<ColumnDef<EventFrameRow, any>[]>(
    () => [
      {
        id: "ts",
        header: tz === "utc" ? "Time (UTC)" : "Time",
        cell: ({ row }) => (
          <span className="font-mono text-xs whitespace-nowrap">{formatTs(row.original.ts, tz)}</span>
        ),
      },
      {
        id: "state",
        header: "State",
        cell: ({ row }) => <span className="text-xs">{row.original.state ?? "—"}</span>,
      },
      {
        id: "event",
        header: "Event",
        cell: ({ row }) => {
          const { sample, response } = row.original;
          const tone = frameTone(row.original);
          // The charger's side leads; the server's answer, when the same frame
          // carries one, follows it as a second line instead of a column.
          const headline = sample || response || "telemetry sample";
          const reply = sample && response ? response : null;
          return (
            <span className="block max-w-[38ch] sm:max-w-[62ch]" title={[sample, response].filter(Boolean).join(" · ") || undefined}>
              <span className={cn("block truncate text-xs", TONE_TEXT_CLASS[tone], !sample && !response && "text-muted-foreground")}>
                {headline}
              </span>
              {reply && (
                <span className="block truncate text-[11px] text-muted-foreground">server: {reply}</span>
              )}
            </span>
          );
        },
      },
      {
        id: "readings",
        header: "V / A / °C",
        cell: ({ row }) => (
          <span className="font-mono text-xs whitespace-nowrap">
            {formatNumber(row.original.voltage)} / {formatNumber(row.original.current)} /{" "}
            {row.original.temperature ?? "—"}
          </span>
        ),
      },
    ],
    [tz],
  );

  return (
    <DataTableShell
      height="auto"
      minHeight="0"
      className="[&>div]:max-h-[480px]"
      footer={
        <>
          <span className="text-xs text-muted-foreground">
            {rows.length.toLocaleString()} frame{rows.length === 1 ? "" : "s"} ·{" "}
            {events.length.toLocaleString()} events shown
            {totalEvents != null ? ` of ${totalEvents.toLocaleString()} derived` : ""}
          </span>
          {hasMore && onLoadMore && (
            <Button variant="outline" size="sm" onClick={onLoadMore} disabled={isLoadingMore}>
              {isLoadingMore ? "Loading…" : "Load more"}
            </Button>
          )}
        </>
      }
    >
      <DataTable
        columns={columns}
        data={rows}
        isLoading={isLoading}
        onRowClick={onSelect ? (row: EventFrameRow) => onSelect(row.primary) : undefined}
        rowClassName={(row: EventFrameRow) => TONE_ROW_CLASS[frameTone(row)]}
        tableClassName="text-xs"
        emptyMessage="No events derived in this window."
      />
    </DataTableShell>
  );
}
